import { Box } from "@chakra-ui/react";
import type { BoxProps } from "@chakra-ui/react";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";

interface FadeInOnViewProps extends Omit<BoxProps, "children"> {
  children: ReactNode;
  /** Delay before the animation starts, in ms. */
  delay?: number;
  /** IntersectionObserver threshold (0 – 1). */
  threshold?: number;
  /** Distance in px the element travels on the Y axis. */
  distance?: number;
  /** Transition duration in seconds. */
  duration?: number;
}

/**
 * Wraps children in a Box that fades in + rises from below when it first
 * enters the viewport. Each instance has its own IntersectionObserver, so
 * elements inside tall sections animate individually as they scroll into
 * view rather than all at once when the section top is crossed.
 */
export function FadeInOnView({
  children,
  delay = 0,
  threshold = 0.2,
  distance = 20,
  duration = 0.8,
  ...boxProps
}: FadeInOnViewProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);

  return (
    <Box
      ref={ref}
      sx={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : `translateY(${distance}px)`,
        transition: `opacity ${duration}s ease-out ${delay}ms, transform ${duration}s ease-out ${delay}ms`,
      }}
      {...boxProps}
    >
      {children}
    </Box>
  );
}
